/**
 * UI State Slice
 * Manages global UI state like sidebar collapse, modals, etc.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface UIState {
    sidebar: {
        isCollapsed: boolean;
        isMinimized: boolean;
        skipTransition: boolean;
    };
}

const initialState: UIState = {
    sidebar: {
        isCollapsed: false,
        isMinimized: false,
        skipTransition: false,
    },
};

export const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        toggleSidebar: (state) => {
            if (state.sidebar.isMinimized) {
                // From minimized: always fully expand
                state.sidebar.isMinimized = false;
                state.sidebar.isCollapsed = false;
            } else {
                state.sidebar.isCollapsed = !state.sidebar.isCollapsed;
            }
            state.sidebar.skipTransition = false;
        },
        minimizeSidebar: (state) => {
            // Fully hidden — triggered automatically by route
            state.sidebar.isMinimized = true;
            state.sidebar.skipTransition = false;
        },
        unminimizeSidebar: (state) => {
            // Restore from minimized — only clears isMinimized, keeps isCollapsed as-is
            state.sidebar.isMinimized = false;
            state.sidebar.skipTransition = false;
        },
        setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
            state.sidebar.isCollapsed = action.payload;
            state.sidebar.isMinimized = false;
            state.sidebar.skipTransition = false;
        },
        collapseSidebar: (state) => {
            state.sidebar.isCollapsed = true;
            state.sidebar.isMinimized = false;
            state.sidebar.skipTransition = false;
        },
        expandSidebar: (state) => {
            state.sidebar.isCollapsed = false;
            state.sidebar.isMinimized = false;
            state.sidebar.skipTransition = false;
        },
        resetSidebarTransition: (state) => {
            state.sidebar.skipTransition = false;
        },
    },
});

export const {
    toggleSidebar,
    minimizeSidebar,
    unminimizeSidebar,
    setSidebarCollapsed,
    collapseSidebar,
    expandSidebar,
    resetSidebarTransition,
} = uiSlice.actions;

export default uiSlice.reducer;
